/**
 * "Legacy" Teams API/Store Module - still in use, but ideally needs to either be
 * updated to a namespaced module, and/or consolidated with teamManagement, etc.
 */
import Nprogress from "nprogress";
import VueNotifications from "vue-notifications";
import apiFactory from "@/helpers/api";
import orderBy from "lodash/orderBy";
import keyBy from "lodash/keyBy";
import TeamType from "@/constants/TeamType";
import TeamMemberStatusType from "@/constants/TeamMemberStatusType";
import { deriveOneOnOneName } from "@/lib/teamUtil";

const { get: getUserAPI, post: postUserAPI, del: deleteUserAPI, } = apiFactory();

function getFreshState() {
	return {
		myTeams: [],
		myOneOnOnes: [],
		lastEditedTeamId: null,
		lastCreatedTeamId: null,
		isEditTeamDialogOpen: false,
		isEditTeamDialogLoading: false,
	};
}

const _state = getFreshState();
const _unobservableState = {};

function ustate( key, value ) {
	if ( value === undefined ) {
		return _unobservableState[ key ];
	}
	_unobservableState[ key ] = value;
}

// getters
const _getters = {
	myTeams: state => {
		return orderBy( state.myTeams, [ "name", "asc", ] );
	},
	myOneOnOnes: ( state, getters, rootState, rootGetters ) => {
		const userProfile = rootGetters[ "userProfile/userProfile" ];
		return orderBy( state.myOneOnOnes, [ "name", "asc", ] ).map( oneOnOne => {
			return {
				...oneOnOne,
				name: deriveOneOnOneName( oneOnOne, userProfile.personId ),
			};
		} );
	},
	myOneOnOnesMap: ( state, getters ) => {
		return keyBy( getters.myOneOnOnes, "id" );
	},
	myFavoriteTeams: ( state, getters ) => {
		const myFavoriteTeams = getters.myTeams
			.filter( team => !!team.isFavorited )
			.map( team => {
				return {
					...team,
					orderKey: team.name.toLowerCase(),
				};
			} );

		return orderBy(
			myFavoriteTeams,
			[ "orderKey", ],
			[ "asc", ]
		);
	},
	myFavoriteOneOnOnes: ( state, getters ) => {
		const myFavoriteOneOnOnes = getters.myOneOnOnes
			.filter( oneOnOne => !!oneOnOne.isFavorited )
			.map( oneOnOne => {
				return {
					...oneOnOne,
					orderKey: oneOnOne.name.toLowerCase(),
				};
			} );

		return orderBy(
			myFavoriteOneOnOnes,
			[ "orderKey", ],
			[ "asc", ]
		);
	},
	lastEditedTeamId: state => state.lastEditedTeamId,
	lastCreatedTeamId: state => state.lastCreatedTeamId,
	isEditTeamDialogOpen: state => state.isEditTeamDialogOpen,
	isEditTeamDialogLoading: state => state.isEditTeamDialogLoading,
};

// actions
const actions = {
	async getTeam( context, teamId ) {
		let results = null;
		try {
			if ( ustate( "loadTeam" ) ) {
				return;
			}

			ustate( "loadTeam", 1 );
			Nprogress.start();

			// fetch teams
			results = await getUserAPI( `/team/${teamId}` );
			context.commit( "updatedTeam", results );
		} catch ( e ) {
			VueNotifications.error( {
				message: e.message || e || "Unexpected error",
			} );
			console.error( e );
		}
		ustate( "loadTeam", 0 );
		Nprogress.done();
		return results;
	},
	async loadTeams( context, { force, } = { force: false, } ) {
		let fetchedTeams = null;
		try {
			if ( ustate( "loadTeams" ) && !force ) {
				return;
			}

			ustate( "loadTeams", 1 );
			Nprogress.start();

			// fetch teams
			fetchedTeams = await getUserAPI( "/team" );
			if ( context.state.lastEditedTeamId ) {
				// refetch the team being edited to refresh its list of
				// members, as the team list API does not populate the
				// members property
				const teamWithMembers = await getUserAPI( `/team/${context.state.lastEditedTeamId}` );
				const teamWithLeads = fetchedTeams.find( team => team.id === context.state.lastEditedTeamId ) || {};
				const teamsNotBeingEdited = fetchedTeams.filter( team => team.id !== teamWithMembers.id );
				// merge team from list and team from fetch to get both
				// members *and* leads
				fetchedTeams = [ ...teamsNotBeingEdited, { ...teamWithLeads, ...teamWithMembers, }, ];
			}
			context.commit( "loadedTeams", fetchedTeams );
			context.commit( "teamManagement/loadedTeams", fetchedTeams );
		} catch ( e ) {
			VueNotifications.error( {
				message: e.message || e || "Unexpected error",
			} );
			console.error( e );
		}
		Nprogress.done();
		return fetchedTeams;
	},
	async saveTeam( context, team ) {
		let teamData = null;

		if ( team.members ) {
			team.members.forEach( m => {
				if ( m.status === "PENDING" ) {
					m.status = TeamMemberStatusType.INVITED;
				}
			} );
			team.members = team.members.filter( m => m !== "DELETED" );
		}

		try {
			Nprogress.start();

			if ( !team.id ) {
				teamData = await postUserAPI( "/team", team );
				context.commit( "createTeam", teamData );
			} else {
				await postUserAPI( `/team/${team.id}`, team );
				const updatedTeam = await getUserAPI( `/team/${team.id}` );
				// reload the team data to ensure we get the updated members
				teamData = { ...team, updatedTeam, };
				context.commit( "updatedTeam", teamData );
			}
		} catch ( e ) {
			VueNotifications.error( {
				message: e.message || e || "Unexpected error",
			} );
			console.error( e );
		}
		Nprogress.done();

		return teamData;
	},
	async acceptTeamInvite( context, teamId ) {
		try {
			Nprogress.start();
			await postUserAPI( `/team/${teamId}/invitation/accept` );
			context.commit( "updateMemberStatus", { id: teamId, status: "ACTIVE", } );
			VueNotifications.success( {
				message: "Invite accepted",
			} );
		} catch ( e ) {
			VueNotifications.error( {
				message: e.message || e || "Unexpected error",
			} );
			console.error( e );
		}
		Nprogress.done();
	},
	async rejectTeamInvite( context, { teamId, personId, } ) {
		try {
			Nprogress.start();
			await deleteUserAPI( `/team/${teamId}/member/${personId}` );
			context.commit( "updateMemberStatus", { id: teamId, status: "DELETED", } );
		} catch ( e ) {
			VueNotifications.error( {
				message: e.message || e || "Unexpected error",
			} );
			console.error( e );
		}
		Nprogress.done();
	},
};

/**
 * Add properties that may be missing from a team object
 * so that we don't have to check for these properties
 * in other places
 * @param team
 * @returns {team}
 */
const normalizeTeam = team => {
	team.members = team.members || [];
	team.memberCount = team.memberCount || ( team?.members?.length || 1 );
	team.leads = team.leads || [];
	return team;
};

// mutations
const mutations = {
	openEditTeamDialog: state => {
		state.isEditTeamDialogOpen = true;
	},
	closeEditTeamDialog: state => {
		state.isEditTeamDialogOpen = false;
		state.lastEditedTeamId = null;
	},
	startEditTeamDialogLoading: state => {
		state.isEditTeamDialogLoading = true;
	},
	stopEditTeamDialogLoading: state => {
		state.isEditTeamDialogLoading = false;
	},
	editTeam( state, teamId ) {
		state.lastEditedTeamId = teamId;
	},
	createTeam( state, createdTeam ) {
		state.lastEditedTeamId = createdTeam.id;
		state.lastCreatedTeamId = createdTeam.id;
	},
	loadedTeams( state, loadedTeams ) {
		state.myTeams = loadedTeams.reduce( ( acc, team ) => {
			if ( team.type === TeamType.TEAM ) {
				acc.push( normalizeTeam( team ) );
			}
			return acc;
		}, [] );
		state.myOneOnOnes = loadedTeams.reduce( ( acc, team ) => {
			if ( team.type === TeamType.ONE_ON_ONE ) {
				acc.push( normalizeTeam( team ) );
			}
			return acc;
		}, [] );
	},
	updateMemberStatus( state, results ) {
		if ( results.status === "DELETED" ) {
			state.myTeams = [ ...state.myTeams.filter( t => t.id !== results.id ), ];
		} else {
			const team = state.myTeams.find( t => t.id === results.id );
			if ( team ) {
				team.memberStatus = results.status;
			}
		}
	},
	updatedTeam( state, updatedTeam ) {
		const otherTeams = state.myTeams.filter( t => t.id !== updatedTeam.id );
		state.myTeams = [ ...otherTeams, normalizeTeam( updatedTeam ), ];
		state.lastEditedTeamId = updatedTeam.id;
	},
	clearLastCreatedTeamId( state ) {
		state.lastCreatedTeamId = null;
	},
	removedMyselfFromTeam( state, { teamId, } ) {
		state.myTeams = state.myTeams.filter( myTeam => myTeam.id !== teamId );
	},
};

export default {
	state: _state,
	getters: _getters,
	actions,
	mutations,
};



// WEBPACK FOOTER //
// ./src/store/modules/api/teams.js